import { Button, Space, Typography } from '@arco-design/web-react';
import FormModal, { FORM_MODAL_WIDTH } from '../FormModal';
import { authUrl } from '../../utils/format';
import { ticketAttachmentUrl, type Ticket, type TicketAttachment } from '../../api/ticket';

export interface TicketAttachmentPreviewModalProps {
  /** 工单详情（附件 URL 需要工单 id；未加载完时为 null，此时不渲染内容） */
  detail: Ticket | null;
  /** 待预览附件（null = 关闭） */
  previewAtt: TicketAttachment | null;
  onClose: () => void;
}

/**
 * 内联预览类型收敛（与后端同一份白名单）：`image/*`（排除 SVG）走 `<img>`，`application/pdf` 走 `<iframe>`，
 * 其余（含 `text/*`）一律不内嵌——访客可控的文本/标记内容被管理台 origin 当顶层文档解析，正是本条要堵的口子。
 * SVG 走降级文案：`<img>` 本身不执行脚本，但后端已把 SVG 移出内联白名单（会带 attachment + nosniff），
 * 前端不按图片渲染，避免两端口径不一、也避免渲染成破图。
 */
function inlinePreviewKind(mimeType?: string): 'image' | 'pdf' | 'none' {
  const mime = String(mimeType || '').split(';')[0].trim().toLowerCase();
  if (mime.startsWith('image/')) return mime.includes('svg') ? 'none' : 'image';
  if (mime === 'application/pdf') return 'pdf';
  return 'none';
}

/** 附件预览弹窗（本页模态框，按 mime 分流展示；不支持的类型引导下载）。 */
export default function TicketAttachmentPreviewModal({ detail, previewAtt, onClose }: TicketAttachmentPreviewModalProps) {
  const kind = previewAtt ? inlinePreviewKind(previewAtt.mime_type) : 'none';
  return (
    <FormModal
      title={`附件预览：${previewAtt?.name ?? ''}`}
      width={FORM_MODAL_WIDTH.wide}
      scroll={false}
      visible={!!previewAtt}
      footer={
        previewAtt && detail ? (
          <Space>
            <Button onClick={() => window.open(authUrl(ticketAttachmentUrl(detail.id, previewAtt.id)), '_blank')}>
              新窗口打开
            </Button>
            <Button type="primary" onClick={onClose}>
              关闭
            </Button>
          </Space>
        ) : null
      }
      onCancel={onClose}
    >
      {previewAtt && detail && (
        kind === 'image' ? (
          <img
            src={authUrl(ticketAttachmentUrl(detail.id, previewAtt.id))}
            alt={previewAtt.name}
            style={{ maxWidth: '100%', maxHeight: 480, objectFit: 'contain', display: 'block', margin: '0 auto' }}
          />
        ) : kind === 'pdf' ? (
          // PDF 走浏览器内置查看器：它需要脚本、并按同源拉取文档，故 sandbox 只放这两个 token——
          // 弹窗/表单/顶层导航全不放行，被嵌文档既改不了父页也带不走标签页（referrer 也不外泄）。
          // 是「能保住 PDF 预览的最小组合」：去 allow-scripts 查看器不渲染，去 allow-same-origin 同源 PDF 拉不到。
          // 注意：sandbox 只在 iframe 插入 DOM 前生效，之后动态改属性无效，故必须写成静态字符串。
          <iframe
            src={authUrl(ticketAttachmentUrl(detail.id, previewAtt.id))}
            title={previewAtt.name}
            sandbox="allow-scripts allow-same-origin"
            referrerPolicy="no-referrer"
            style={{ width: '100%', height: 480, border: 'none' }}
          />
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            该文件类型（{previewAtt.mime_type || '未知'}）不支持内嵌预览，请使用「新窗口打开」查看
          </Typography.Text>
        )
      )}
    </FormModal>
  );
}
